import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Typography, Button, Dialog, Grid, DialogTitle, Switch, Tooltip, IconButton } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePatchProductContentMutation, useDeleteProductContentMutation } from '../features/api/rest/apiProductContentsSlice';
import ProductOrganizationBoardDialog from './ProductOrganizationBoardDialog';

function ProductOrganizationBoardCard({ id, content, isPublic, permission }) {
  const { opk } = useParams();
  const { ean } = useParams();
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [patchContent] = usePatchProductContentMutation();
  const [deleteContent] = useDeleteProductContentMutation();
  const changeVisibility = async () => {
    setIsUpdating(true);
    try {
      await patchContent({ opk, ean, id, payload: { public: !isPublic } });
    } finally {
      setIsUpdating(false);
    }
  };
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteContent({ opk, ean, id });
    } finally {
      setIsDeleting(false);
    }
  };
  return (
    <>
      <Card sx={{ border: '1px solid #d3d4d5', my: 4 }}>
        <CardContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {content}
          </Typography>
        </CardContent>
        {permission() && (
          <Grid container alignItems="center" sx={{ ml: 2, display: 'flex', justifyContent: 'left', mb: 2 }}>
            <Grid item>
              <Switch checked={isPublic} onChange={changeVisibility} disabled={isUpdating} />
            </Grid>
            <Grid item>
              <Typography>{t(`organizations.product.board.public`)}</Typography>
            </Grid>
            <Grid item>
              <Tooltip title={t('common.actions.edit')} placement="top">
                <IconButton onClick={() => setIsEditing(true)} sx={{ ml: 2 }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item>
              <Tooltip title={t('common.actions.delete')} placement="top">
                <IconButton
                  onClick={() => {
                    setIsDeleting(true);
                  }}
                  sx={{ ml: 2 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
      </Card>
      <ProductOrganizationBoardDialog ean={ean} actionType="edit" isOpen={isEditing} setIsOpen={setIsEditing} id={id} oldContent={content} oldPublic={isPublic} />
      <Dialog open={isDeleting} onClose={() => setIsDeleting(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>{t('organizations.product.board.delete')}</DialogTitle>
        <Typography variant="body2" mx={4}>
          {t('organizations.product.board.deleteMessage')}
        </Typography>
        <Grid display="flex" justifyContent="flex-end" gap={1} p={2} pb={3}>
          <Button type="submit" onClick={() => setIsDeleting(false)} variant="outlined" color="secondary">
            {t('common.actions.close')}
          </Button>
          <Button type="submit" onClick={handleDelete} variant="contained" color="warning">
            {t('common.actions.delete')}
          </Button>
        </Grid>
      </Dialog>
    </>
  );
}

ProductOrganizationBoardCard.propTypes = {
  id: PropTypes.number.isRequired,
  content: PropTypes.string.isRequired,
  isPublic: PropTypes.bool.isRequired,
  permission: PropTypes.func.isRequired,
};

export default ProductOrganizationBoardCard;
